/**
 * LOCALE / CURRENCY / TIME ZONE
 *
 * Single hook + context that knows the active user's locale, currency,
 * and preferred time zone. Built on top of the browser's `Intl` APIs so
 * we add zero kilobytes of dependencies.
 *
 * Defaults:
 *   currency    -> user.currency || user.profile?.currency || 'USD'
 *   locale      -> user.locale   || navigator.language       || 'en-US'
 *   timeZone    -> user.time_zone|| Intl.DateTimeFormat().resolvedOptions().timeZone
 *
 * Exports:
 *   <LocaleProvider user={user}> — wrap the app
 *   useLocale()     — full context
 *   useCurrency()   — { code, format(amount), symbol }
 *   <TimeZoneAware value={isoDate} /> — renders a localized date/time
 */
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'USD';

const detectTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const detectLocale = () => {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return DEFAULT_LOCALE;
};

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  currency: DEFAULT_CURRENCY,
  timeZone: 'UTC',
  setLocale: () => {},
  setCurrency: () => {},
  setTimeZone: () => {},
});

export const LocaleProvider = ({ children, user }) => {
  // Seed from user profile if present, otherwise sniff from browser.
  const [locale, setLocale] = useState(
    () => user?.locale || detectLocale() || DEFAULT_LOCALE
  );
  const [currency, setCurrency] = useState(
    () =>
      user?.currency ||
      user?.profile?.currency ||
      user?.preferred_currency ||
      DEFAULT_CURRENCY
  );
  const [timeZone, setTimeZone] = useState(
    () =>
      user?.time_zone ||
      user?.timezone ||
      user?.profile?.time_zone ||
      detectTimeZone()
  );

  // Re-seed whenever the user object changes (post-login, profile update).
  useEffect(() => {
    if (!user) return;
    if (user.locale) setLocale(user.locale);
    const nextCurrency =
      user.currency || user.profile?.currency || user.preferred_currency;
    if (nextCurrency) setCurrency(nextCurrency);
    const nextTz = user.time_zone || user.timezone || user.profile?.time_zone;
    if (nextTz) setTimeZone(nextTz);
  }, [user]);

  const value = useMemo(
    () => ({
      locale,
      currency,
      timeZone,
      setLocale,
      setCurrency,
      setTimeZone,
    }),
    [locale, currency, timeZone]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export const useLocale = () => useContext(LocaleContext);

/**
 * Currency hook. Returns a memoized formatter keyed off the active
 * locale + currency so components don't rebuild it on every render.
 *
 * Usage:
 *   const { format, code } = useCurrency();
 *   <span>{format(1299)}</span> // -> "$1,299" / "€1.299" / ...
 */
export const useCurrency = (overrideCurrency) => {
  const { locale, currency } = useLocale();
  const code = overrideCurrency || currency || DEFAULT_CURRENCY;
  return useMemo(() => {
    let formatter;
    let compactFormatter;
    try {
      formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      compactFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        notation: 'compact',
        maximumFractionDigits: 1,
      });
    } catch {
      formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
        style: 'currency',
        currency: DEFAULT_CURRENCY,
        minimumFractionDigits: 0,
      });
      compactFormatter = formatter;
    }
    return {
      code,
      locale,
      format: (amount) => {
        if (amount == null || Number.isNaN(Number(amount))) return '—';
        return formatter.format(Number(amount));
      },
      formatCompact: (amount) => {
        if (amount == null || Number.isNaN(Number(amount))) return '—';
        return compactFormatter.format(Number(amount));
      },
    };
  }, [locale, code]);
};

/**
 * Low-level helper for any file that needs currency formatting but
 * doesn't want to pull in the whole hook (e.g. sample data helpers).
 */
export const formatCurrencyIntl = (amount, { locale, currency } = {}) => {
  try {
    return new Intl.NumberFormat(locale || DEFAULT_LOCALE, {
      style: 'currency',
      currency: currency || DEFAULT_CURRENCY,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch {
    return `$${Number(amount || 0).toLocaleString()}`;
  }
};

// ---------------------------------------------------------------------------
// <TimeZoneAware />
// ---------------------------------------------------------------------------

/**
 * Renders a date in the viewer's locale + the active time zone. Useful
 * for contract deadlines and deliverable due dates where "5pm" means
 * something completely different depending on where you are.
 *
 * Props:
 *   value          ISO string or Date
 *   mode           'date' (default) | 'time' | 'datetime' | 'relative'
 *   showTimeZone   appends " (America/Los_Angeles)" when true
 */
export const TimeZoneAware = ({
  value,
  mode = 'datetime',
  showTimeZone = false,
  className = '',
}) => {
  const { locale, timeZone } = useLocale();
  if (!value) return <span className={className}>—</span>;

  const date =
    typeof value === 'string' || typeof value === 'number'
      ? new Date(value)
      : value;
  if (Number.isNaN(date?.getTime?.())) {
    return <span className={className}>—</span>;
  }

  let text;
  try {
    if (mode === 'relative') {
      const diffSec = Math.round((date.getTime() - Date.now()) / 1000);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const units = [
        ['year', 31536000],
        ['month', 2592000],
        ['day', 86400],
        ['hour', 3600],
        ['minute', 60],
      ];
      let picked = null;
      for (const [unit, seconds] of units) {
        if (Math.abs(diffSec) >= seconds) {
          picked = [unit, Math.round(diffSec / seconds)];
          break;
        }
      }
      text = picked
        ? rtf.format(picked[1], picked[0])
        : rtf.format(diffSec, 'second');
    } else {
      const opts =
        mode === 'date'
          ? { year: 'numeric', month: 'short', day: 'numeric', timeZone }
          : mode === 'time'
            ? { hour: 'numeric', minute: '2-digit', timeZone }
            : {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZone,
              };
      text = new Intl.DateTimeFormat(locale, opts).format(date);
    }
  } catch {
    text = date.toISOString();
  }

  return (
    <time
      dateTime={date.toISOString()}
      className={className}
      title={`${date.toISOString()} (${timeZone})`}
    >
      {text}
      {showTimeZone && mode !== 'relative' && (
        <span className="text-slate-400 ml-1">({timeZone})</span>
      )}
    </time>
  );
};

export default LocaleContext;
